// Selection Script for Visual Edit
// This script is injected into the iframe to enable element selection

export const SELECTION_SCRIPT = `
(function() {
  // Prevent double initialization
  if (window.__pixelSelectionMode !== undefined) return;
  
  window.__pixelSelectionMode = false;
  let hoverOverlay = null;
  let currentHoveredElement = null;
  
  // Create hover overlay element
  function createOverlay() {
    if (hoverOverlay) return;
    hoverOverlay = document.createElement('div');
    hoverOverlay.id = '__pixel-hover-overlay';
    hoverOverlay.style.cssText = \`
      position: fixed;
      pointer-events: none;
      z-index: 999999;
      border: 2px solid #FEB1FF;
      background: rgba(254, 177, 255, 0.1);
      border-radius: 4px;
      transition: all 0.1s ease;
      box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.1);
    \`;
    document.body.appendChild(hoverOverlay);
  }
  
  function removeOverlay() {
    if (hoverOverlay) {
      hoverOverlay.remove();
      hoverOverlay = null;
    }
    currentHoveredElement = null;
  }
  
  function positionOverlay(rect) {
    if (!hoverOverlay) return;
    hoverOverlay.style.top = rect.top + 'px';
    hoverOverlay.style.left = rect.left + 'px';
    hoverOverlay.style.width = rect.width + 'px';
    hoverOverlay.style.height = rect.height + 'px';
    hoverOverlay.style.display = 'block';
  }
  
  // Get XPath for element
  function getXPath(element) {
    if (!element) return '';
    if (element.id) return '//*[@id="' + element.id + '"]';
    
    if (element === document.body) return '/html/body';
    
    let ix = 0;
    const siblings = element.parentNode ? element.parentNode.childNodes : [];
    for (let i = 0; i < siblings.length; i++) {
      const sibling = siblings[i];
      if (sibling === element) {
        const parentPath = getXPath(element.parentNode);
        const tagName = element.tagName.toLowerCase();
        return parentPath + '/' + tagName + '[' + (ix + 1) + ']';
      }
      if (sibling.nodeType === 1 && sibling.tagName === element.tagName) {
        ix++;
      }
    }
    return '';
  }
  
  // Get relevant computed styles
  function getRelevantStyles(element) {
    const computed = window.getComputedStyle(element);
    return {
      backgroundColor: computed.backgroundColor,
      color: computed.color,
      fontSize: computed.fontSize,
      fontFamily: computed.fontFamily,
      fontWeight: computed.fontWeight,
      padding: computed.padding,
      margin: computed.margin,
      borderRadius: computed.borderRadius,
      border: computed.border,
      boxShadow: computed.boxShadow,
      textAlign: computed.textAlign,
      display: computed.display,
      flexDirection: computed.flexDirection,
      justifyContent: computed.justifyContent,
      alignItems: computed.alignItems,
      gap: computed.gap
    };
  }
  
  // Get data attributes
  function getDataAttributes(element) {
    const attrs = {};
    for (const attr of element.attributes) {
      if (attr.name.startsWith('data-')) {
        attrs[attr.name] = attr.value;
      }
    }
    return attrs;
  }

  // Get element by XPath (helper function)
  function getElementByXPath(path) {
    return document.evaluate(path, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
  }
  
  // Handle mouse move - highlight element
  function handleMouseMove(e) {
    if (!window.__pixelSelectionMode) return;
    
    const target = e.target;
    
    // Ignore our own overlay
    if (target.id === '__pixel-hover-overlay') return;
    
    // Ignore body and html
    if (target === document.body || target === document.documentElement) return;
    
    if (target !== currentHoveredElement) {
      currentHoveredElement = target;
      createOverlay();
      positionOverlay(target.getBoundingClientRect());
    }
  }
  
  // Handle click - select element
  function handleClick(e) {
    if (!window.__pixelSelectionMode) return;
    
    const target = e.target;
    
    // Ignore our own overlay
    if (target.id === '__pixel-hover-overlay') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = target.getBoundingClientRect();
    
    // Get lightweight children info
    const children = [];
    if (target.children && target.children.length > 0) {
      for (let i = 0; i < target.children.length; i++) {
        const child = target.children[i];
        // Skip hidden elements or scripts/styles if needed
        if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') continue;
        
        children.push({
          tagName: child.tagName,
          className: child.className ? (typeof child.className === 'string' ? child.className : '') : '',
          xpath: getXPath(child),
          textContent: child.textContent ? child.textContent.trim().slice(0, 50) : ''
        });
      }
    }

    const elementInfo = {
      tagName: target.tagName,
      className: target.className || '',
      id: target.id || undefined,
      xpath: getXPath(target),
      textContent: target.textContent ? target.textContent.trim().slice(0, 200) : undefined,
      children: children,
      imageSrc: target.tagName === 'IMG' ? target.src : undefined,
      boundingRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      },
      computedStyles: getRelevantStyles(target),
      dataAttributes: getDataAttributes(target)
    };
    
    // Send to parent
    window.parent.postMessage({ 
      type: 'PIXEL_ELEMENT_SELECTED', 
      data: elementInfo 
    }, '*');
    
    // Disable selection mode after selecting
    window.__pixelSelectionMode = false;
    // Don't remove overlay immediately so user sees what they selected
    // It will be removed when they select something else or cancel
  }
  
  // Listen for messages from parent
  window.addEventListener('message', (e) => {
    if (e.data.type === 'PIXEL_TOGGLE_SELECTION') {
      window.__pixelSelectionMode = e.data.enabled;
      if (!e.data.enabled) {
        removeOverlay();
      }
    }

    // Force select element by XPath (from Visual Edit Panel drill-down)
    if (e.data.type === 'PIXEL_SELECT_BY_XPATH') {
      const { xpath } = e.data;
      const element = getElementByXPath(xpath);
      if (element) {
        // Simulate click/selection
        const rect = element.getBoundingClientRect();
        currentHoveredElement = element;
        createOverlay();
        positionOverlay(rect);
        
        // Reuse extraction logic (simplified here to avoid duplication, strictly we should refactor)
        // For now, let's just trigger the extraction logic
        // We need to construct a fake event or abstract the extraction
        
        // Abstracted extraction:
        const children = [];
        if (element.children && element.children.length > 0) {
          for (let i = 0; i < element.children.length; i++) {
            const child = element.children[i];
            if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') continue;
            children.push({
              tagName: child.tagName,
              className: child.className ? (typeof child.className === 'string' ? child.className : '') : '',
              xpath: getXPath(child),
              textContent: child.textContent ? child.textContent.trim().slice(0, 50) : ''
            });
          }
        }

        const elementInfo = {
          tagName: element.tagName,
          className: element.className || '',
          id: element.id || undefined,
          xpath: xpath,
          textContent: element.textContent ? element.textContent.trim().slice(0, 200) : undefined,
          children: children,
          imageSrc: element.tagName === 'IMG' ? element.src : undefined,
          boundingRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
          computedStyles: getRelevantStyles(element),
          dataAttributes: getDataAttributes(element)
        };
        
        window.parent.postMessage({ type: 'PIXEL_ELEMENT_SELECTED', data: elementInfo }, '*');
        window.__pixelSelectionMode = false;
      }
    }
    
    // Apply live style changes from editor
    if (e.data.type === 'PIXEL_APPLY_STYLES') {
      const { xpath, styles } = e.data;
      const element = getElementByXPath(xpath);
      if (element && styles) {
        Object.keys(styles).forEach(key => {
          element.style[key] = styles[key];
        });
        console.log('[Pixel] Applied live styles to', xpath, styles);
      }
    }
    
    // Reset styles (when cancelling edit)
    if (e.data.type === 'PIXEL_RESET_STYLES') {
      const { xpath } = e.data;
      const element = getElementByXPath(xpath);
      if (element) {
        element.style.cssText = ''; // Reset inline styles
        console.log('[Pixel] Reset styles for', xpath);
      }
    }
    
    // Update text content
    if (e.data.type === 'PIXEL_UPDATE_CONTENT') {
      const { xpath, content, imageSrc } = e.data;
      const element = getElementByXPath(xpath);
      if (element) {
        if (imageSrc && element.tagName === 'IMG') {
          element.src = imageSrc;
        } else if (content !== undefined) {
          element.textContent = content;
        }
        console.log('[Pixel] Updated content for', xpath);
      }
    }
  });
  
  // Add event listeners
  document.addEventListener('mousemove', handleMouseMove, true);
  document.addEventListener('click', handleClick, true);
  
  // Notify parent that script is ready
  window.parent.postMessage({ type: 'PIXEL_SELECTION_READY' }, '*');
  
  console.log('[Pixel] Visual Edit selection script loaded');
})();
`;

// Script to inject that tells parent the iframe is ready
export const INJECT_SELECTION_SCRIPT = `
<script>
${SELECTION_SCRIPT}
</script>
`;
